import React, { useEffect } from 'react';
import { Row, Col, FormGroup, Label, Input, FormFeedback } from 'reactstrap';
import Select from 'react-select';
import * as yup from "yup";
import { useFormik } from "formik";
import { useDispatch, useSelector } from 'react-redux';
import { selectBookingFormData, selectPostcodes, updateCustomerDetails } from 'slices/booking/bookingSlice';
import { selectIsDiscountedBooking, selectIsEdit } from 'slices/booking/cartSlice';

interface FormValues {
  name: string;
  email: string;
  address_line_1: string;
  address_line_2: string;
  town: string;
  complete_postcode: string;
  phone_no_home: string;
  phone_no_mobile: string;
  receive_marketing_emails: boolean;
  postcode_id: number;
}

function CustomerDetailsSection() {
  const dispatch = useDispatch();
  const postcodes = useSelector(selectPostcodes);
  const customerDetails = useSelector(selectBookingFormData);
  const isEdit = useSelector(selectIsEdit);
  const isDiscountedBooking = useSelector(selectIsDiscountedBooking); 
  

  const formik = useFormik<FormValues>({
    initialValues: isEdit ? {
      name: customerDetails?.name || '',
      email: customerDetails?.email || '',
      address_line_1: customerDetails?.address_line_1 || '',
      address_line_2: customerDetails?.address_line_2 || '',
      town: customerDetails?.town || '',
      complete_postcode: customerDetails?.complete_postcode || '',
      phone_no_home: customerDetails.phone_no_home || '',
      phone_no_mobile: customerDetails.phone_no_mobile || '',
      receive_marketing_emails: customerDetails.receive_marketing_emails === 1 || false,
      postcode_id: customerDetails.postcode_id || 0,
    } :{
      name: '',
      email: '',
      address_line_1: '',
      address_line_2: '',
      town: '',
      complete_postcode: '',
      phone_no_home: '',
      phone_no_mobile: '',
      receive_marketing_emails: false,
      postcode_id: 0, // Changed from null to 0
    },
    validationSchema: yup.object().shape({
      name: yup.string().required('Please enter customer name'),
      email: yup.string().email('Please enter a valid email address').required('Please enter email address'),
      address_line_1: yup.string().required('Please enter address line 1'),
      address_line_2: yup.string(),
      town: yup.string().required('Please enter town'),
      complete_postcode: yup.string().required('Please enter complete postcode'),
      phone_no_home: yup.string(),
      phone_no_mobile: yup.string().required('Please enter phone number'),
      receive_marketing_emails: yup.boolean(),
      postcode_id: yup.number().required('Please select a postcode'),
    }),
    onSubmit: values => {
      // Submit logic here
    },
    validateOnMount: true,
    validateOnBlur: true,
    validateOnChange: true,
  });

  useEffect(() => {
    const customerDetails = {
      ...formik.values,
      postcode_id: formik.values.postcode_id || undefined // Convert 0 to undefined if not selected
    };
    dispatch(updateCustomerDetails(customerDetails));
  }, [formik.values, dispatch]);

  return (
    <form>
      <Row>
        <Col md={6}>
          <FormGroup className="form-group">
            <Label for="name">Your Full Name: <span className="text-danger">*</span></Label>
            <Input
              id="name"
              name="name"
              type="text"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.name}
              invalid={formik.touched.name && formik.errors.name ? true : false}
            />
            {formik.touched.name && formik.errors.name && (
              <FormFeedback className="error-message">{formik.errors.name}</FormFeedback>
            )}
          </FormGroup>
        </Col>

         <Col md={6}>          
         <FormGroup className="form-group">
            <Label for="email">Email Address: <span className="text-danger">*</span></Label>
            <Input
              id="email"
              name="email"
              type="email"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.email}
              invalid={formik.touched.email && formik.errors.email ? true : false}
            />
            {formik.touched.email && formik.errors.email && (
              <FormFeedback className="error-message">{formik.errors.email}</FormFeedback>
            )}
          </FormGroup>
        </Col>

        <Col md={6}>
          <FormGroup className="form-group">
            <Label for="postcode_id">Area code: <span className="text-danger">*</span></Label>
            <Select
              isDisabled={isDiscountedBooking}
              placeholder=""
              className={
                formik.touched.postcode_id && formik.errors.postcode_id
                  ? "is-invalid"
                  : ""
              }
              options={postcodes.map(postcode => ({
                value: postcode.id.toString(),
                label: postcode.postcode
              }))}
              value={postcodes.map(postcode => ({
                value: postcode.id.toString(),
                label: postcode.postcode
              })).find(option => option.value === (formik.values.postcode_id?.toString() || ''))}
              onChange={(selectedOption) => {
                if (selectedOption) {
                  const selectedPostcode = postcodes.find(p => p.id.toString() === selectedOption.value);
                  if (selectedPostcode) {
                    formik.setFieldValue('postcode_id', parseInt(selectedOption.value));
                    formik.setFieldValue('town', selectedPostcode.town);
                    formik.setFieldValue('address_line_2', selectedPostcode.region);
                    formik.setFieldValue('complete_postcode', selectedPostcode.postcode + "-");
                  }
                }
              }}
            />
            {formik.touched.postcode_id && formik.errors.postcode_id && (
              <FormFeedback className="error-message">{formik.errors.postcode_id}</FormFeedback>
            )}
          </FormGroup>
        </Col>

        <Col md={6}>
          <FormGroup className="form-group">
            <Label for="complete_postcode">Postcode: <span className="text-danger">*</span></Label>
            <Input
              id="complete_postcode"
              name="complete_postcode"
              type="text"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.complete_postcode}
              invalid={formik.touched.complete_postcode && formik.errors.complete_postcode ? true : false}
            />
            {formik.touched.complete_postcode && formik.errors.complete_postcode && (
              <FormFeedback className="error-message">{formik.errors.complete_postcode}</FormFeedback>
            )}
          </FormGroup>
        </Col>

        <Col md={12}>
          <FormGroup className="form-group">
            <Label for="address_line_1">Address Line 1: <span className="text-danger">*</span></Label>
            <Input
              id="address_line_1"
              name="address_line_1"
              type="text"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.address_line_1}
              invalid={formik.touched.address_line_1 && formik.errors.address_line_1 ? true : false}
            />
            {formik.touched.address_line_1 && formik.errors.address_line_1 && (
              <FormFeedback className="error-message">{formik.errors.address_line_1}</FormFeedback>
            )}
          </FormGroup>
        </Col>

        <Col md={6}>
          <FormGroup className="form-group">
            <Label for="address_line_2">Address Line 2:</Label>
            <Input
              id="address_line_2"
              name="address_line_2"
              type="text"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.address_line_2}
            />
          </FormGroup>
        </Col>

        <Col md={6}>
          <FormGroup className="form-group">
            <Label for="town">Town: <span className="text-danger">*</span></Label>
            <Input
              id="town"
              name="town"
              type="text"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.town}
              invalid={formik.touched.town && formik.errors.town ? true : false}
            />
            {formik.touched.town && formik.errors.town && (
              <FormFeedback className="error-message">{formik.errors.town}</FormFeedback>
            )}
          </FormGroup>
        </Col>

        <Col md={6}>
          <FormGroup className="form-group">
            <Label for="phone_no_mobile">Phone Number (mobile): <span className="text-danger">*</span></Label>
            <Input
              id="phone_no_mobile"
              name="phone_no_mobile"
              type="tel"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.phone_no_mobile}
              invalid={formik.touched.phone_no_mobile && formik.errors.phone_no_mobile ? true : false}
            />
            {formik.touched.phone_no_mobile && formik.errors.phone_no_mobile && (
              <FormFeedback className="error-message">{formik.errors.phone_no_mobile}</FormFeedback>
            )}
          </FormGroup>
        </Col>

        <Col md={6}>
          <FormGroup className="form-group">
            <Label for="phone_no_home">Phone Number (home):</Label>
            <Input
              id="phone_no_home"
              name="phone_no_home"
              type="tel"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.phone_no_home}
              invalid={formik.touched.phone_no_home && formik.errors.phone_no_home ? true : false}
            />
            {formik.touched.phone_no_home && formik.errors.phone_no_home && (
              <FormFeedback className="error-message">{formik.errors.phone_no_home}</FormFeedback>
            )}
          </FormGroup>
        </Col>
        <Col>
         <FormGroup check className="mb-3">
            <Input
              type="checkbox"
              id="receive_marketing_emails"
              name="receive_marketing_emails"
              onChange={formik.handleChange}
              checked={formik.values.receive_marketing_emails}
            />
            <Label check for="receive_marketing_emails">
              Send marketing emails
            </Label>
          </FormGroup>
        </Col>
      </Row>
    </form>
  );
}

export default CustomerDetailsSection;
